import {
  Avatar,
  AvatarGroup,
  Badge,
  Button,
  Card,
  CardBody,
  Stat,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@one-nexty/components-web";

import { Preview } from "../ui/preview";
import { Section, SubSection } from "../ui/section";
import { ChartIcon, UsersIcon } from "../ui/icons";

const MEMBERS = [
  { name: "Camille Roy", role: "Design", status: "Actif", tone: "success" as const },
  { name: "Théo Marchand", role: "Ingénierie", status: "Actif", tone: "success" as const },
  { name: "Inès Bakouche", role: "Produit", status: "Invité", tone: "warning" as const },
  { name: "Lucas Ferreira", role: "Ingénierie", status: "Suspendu", tone: "danger" as const },
];

export function DataSection() {
  return (
    <Section
      id="donnees"
      eyebrow="Données"
      title="Avatar, Stat & Table"
      description="Le tableau vit dans une région défilable et focusable : sans elle, un tableau large est inaccessible au clavier seul (WCAG 2.1.1)."
    >
      <SubSection title="Avatars" hint="Repli sur les initiales si l'image échoue">
        <Preview
          label="avatar.tsx"
          code={`<Avatar name="Camille Roy" size="sm" />
<Avatar name="Inès Bakouche" size="md" status="online" />
<Avatar name="Nexty" size="xl" shape="square" />
<Avatar src={photo} name="Repli initiales" size="md" />`}
        >
          <Avatar name="Camille Roy" size="xs" />
          <Avatar name="Théo Marchand" size="sm" />
          <Avatar name="Inès Bakouche" size="md" status="online" />
          <Avatar name="Lucas Ferreira" size="lg" status="busy" />
          <Avatar name="Nexty" size="xl" shape="square" />
          <Avatar src="https://exemple.invalid/photo.jpg" name="Image cassée" size="md" />
        </Preview>
      </SubSection>

      <SubSection title="Groupe d'avatars">
        <Preview
          label="avatar-group.tsx"
          code={`<AvatarGroup max={3}>
  {members.map((member) => (
    <Avatar key={member.name} name={member.name} />
  ))}
</AvatarGroup>`}
        >
          <AvatarGroup max={3}>
            {MEMBERS.map((member) => (
              <Avatar key={member.name} name={member.name} />
            ))}
          </AvatarGroup>
        </Preview>
      </SubSection>

      <SubSection title="Indicateurs" hint="invertDelta quand la baisse est une bonne nouvelle">
        <Preview
          layout="stack"
          label="stats.tsx"
          code={`<Card>
  <CardBody>
    <Stat label="Composants" value="24" delta={140} hint="depuis la v0.1.0" icon={<ChartIcon />} />
  </CardBody>
</Card>

{/* Sur un poids de bundle, la baisse est une bonne nouvelle */}
<Stat label="Poids du bundle" value="42 ko" delta={-8} invertDelta hint="gzip" />`}
        >
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardBody>
                <Stat
                  label="Composants"
                  value="24"
                  delta={140}
                  hint="depuis la v0.1.0"
                  icon={<ChartIcon />}
                />
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <Stat label="Design tokens" value="134" delta={17} icon={<ChartIcon />} />
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <Stat label="Membres actifs" value="18" delta={-4} icon={<UsersIcon />} />
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <Stat label="Poids du bundle" value="42 ko" delta={-8} invertDelta hint="gzip" />
              </CardBody>
            </Card>
          </div>
        </Preview>
      </SubSection>

      <SubSection title="Tableau">
        <Preview
          layout="stack"
          label="table.tsx"
          code={`<Table caption="Membres de l'organisation">
  <TableHead>
    <TableRow>
      <TableHeaderCell>Membre</TableHeaderCell>
      <TableHeaderCell align="end">Actions</TableHeaderCell>
    </TableRow>
  </TableHead>
  <TableBody>
    {members.map((member) => (
      <TableRow key={member.name} selected={member.current}>
        <TableCell>{member.name}</TableCell>
        <TableCell align="end">
          <Button variant="ghost" size="xs">Modifier</Button>
        </TableCell>
      </TableRow>
    ))}
  </TableBody>
</Table>`}
        >
          <Table caption="Membres de l'organisation">
            <TableHead>
              <TableRow>
                <TableHeaderCell>Membre</TableHeaderCell>
                <TableHeaderCell>Rôle</TableHeaderCell>
                <TableHeaderCell>Statut</TableHeaderCell>
                <TableHeaderCell align="end">Actions</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {MEMBERS.map((member, index) => (
                <TableRow key={member.name} selected={index === 1}>
                  <TableCell>
                    <span className="flex items-center gap-3">
                      <Avatar name={member.name} size="sm" />
                      <span className="font-medium">{member.name}</span>
                    </span>
                  </TableCell>
                  <TableCell className="text-fg-muted">{member.role}</TableCell>
                  <TableCell>
                    <Badge tone={member.tone} dot>
                      {member.status}
                    </Badge>
                  </TableCell>
                  <TableCell align="end">
                    <Button variant="ghost" size="xs">
                      Modifier
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Preview>
      </SubSection>
    </Section>
  );
}
